import React, { useState } from "react";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GamePublicationState, GameVisibilityState, useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import { DndTable } from "@components/DND";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal, OverflowArea, YesNoColumn } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function Relationship(props) {
    const params = useParams();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (params.rel_type) {
        if (params.rel_type === "bundle") {
            return <BundleAdd {...props} />;
        } else if (params.rel_type === "dlc") {
            return <DlcAdd {...props} />;
        } else if (params.rel_type === "playtest") {
            return <PlaytestAdd {...props} />;
        } else if (params.rel_type === "edition") {
            return <EditionAdd {...props} />;
        }
    } else {
        if (gameCommonInfo.game.is_bundle) {
            return <BundleItemList {...props} />;
        } else if (gameCommonInfo.game.is_edition) {
            return <EditionItemList {...props} />;
        }
        return <RelationshipList {...props} />;
    }
}

const RelationshipList = ({ gameId }) => {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:game:relationship", { game_id: gameId }));
    const commonInfo = useRouteLoaderData("commonInfo");

    if (!apiData) return;

    const bundleHeaderTools = (
        <Link
            to={url_reverse("games:relationship:add", { game_id: gameId, rel_type: "bundle" })}
            className="btn btn-sm btn-dark"
        >
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Link>
    );

    return (
        <>
            <DemoBlock apiData={apiData} />
            <PlaytestBlock apiData={apiData} setApiData={setApiData} />
            {commonInfo.account.is_super && <EditionBlock apiData={apiData} setApiData={setApiData} />}
            <ListingCard header={t("Наборы")} headerTools={bundleHeaderTools}>
                <BundleListSortable apiData={apiData} setApiData={setApiData} />
            </ListingCard>
            <DlcBlock apiData={apiData} setApiData={setApiData} />
        </>
    );
};

function InnerItemAddModal({ apiData, setApiData, showModal, setShowModal }) {
    const { t } = useTranslation();
    const { apiUrl, setApiReget } = useAppContext();
    const [search, setSearch] = useState("");
    const { api_post } = useRequest();

    const handleClose = () => {
        setShowModal(false);
    };

    const handleApply = () => {
        const ids = apiData.available_games.filter((o) => o.__required_item || o.chosen).map((o) => o.id);
        if (ids.length) {
            api_post(null, apiUrl, {
                params: { action: "add_items", ids },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiReget(true);
                    }
                },
            });
        }
        handleClose();
    };

    return (
        <DevModal
            size="lg"
            show={showModal}
            onHide={handleClose}
            header={t("Выбрать продукты")}
            body={
                <>
                    <Row className="mb-2">
                        <Col md={12}>
                            <DevForm.TextInput
                                autoFocus
                                value={search}
                                placeholder={t("Поиск")}
                                onChange={(ev) => setSearch(ev.target.value)}
                            />
                        </Col>
                    </Row>
                    <OverflowArea y={500}>
                        <Table size="sm" hover responsive className="mt-2">
                            <thead>
                                <tr>
                                    <th />
                                    <th>ID</th>
                                    <th width="90%">{t("Название")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiData.available_games?.map((game, ind) => {
                                    if (apiData.object_list.map((o) => o.target_id).includes(game.id)) {
                                        return null;
                                    }
                                    if (search && game.name.toLowerCase().indexOf(search.toLowerCase()) < 0) {
                                        return null;
                                    }
                                    return (
                                        <tr key={game.id}>
                                            <td>
                                                <DevForm.Checkbox
                                                    checked={game.__required_item || game.chosen || false}
                                                    disabled={game.__required_item}
                                                    onChange={(ev) => {
                                                        const newApiData = { ...apiData };
                                                        newApiData.available_games[ind].chosen = ev.target.checked;
                                                        setApiData(newApiData);
                                                    }}
                                                />
                                            </td>
                                            <td>{game.id}</td>
                                            <td>{game.name}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </OverflowArea>
                </>
            }
            footer={
                <Button variant="primary" onClick={handleApply}>
                    {t("Применить")}
                </Button>
            }
        />
    );
}

// ---------- Bundle ----------

const BundleAdd = ({ gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:bundle:add", { game_id: gameId }));

    if (!apiData) return;

    return (
        <CommonCard header={t("Добавить набор")}>
            <RenderForm backUrl={url_reverse("games:relationship", { game_id: gameId })} />
        </CommonCard>
    );
};

function BundleListSortable({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const gameId = gameCommonInfo.game.id;
    const GameRelationshipKindTrans = useChoiceLabel("GameRelationshipKind");

    return (
        <DndTable
            apiData={apiData}
            setApiData={setApiData}
            apiUrl={url_reverse("api:game:relationship", { game_id: gameId })}
            listName="bundle_list"
            postAction="rearrange_bundle"
            ths={
                <>
                    <th>{t("Название")}</th>
                    <th>{t("Тип")}</th>
                    <th>{t("Статус")}</th>
                    <th />
                </>
            }
            tds={(el) => (
                <>
                    <td>
                        <Link
                            to={url_reverse("games:locale:edit", {
                                game_id: el.target_id,
                                locale: el.target.base_locale,
                            })}
                        >
                            {el.target.base_locale_object.name}
                        </Link>
                    </td>
                    <td>{GameRelationshipKindTrans[el.kind]}</td>
                    <td>
                        {el.__allow_buy_soft ? (
                            <Badge variant="success">{t("Активный")}</Badge>
                        ) : (
                            <Badge variant="danger">{t("Неактивный")}</Badge>
                        )}
                    </td>
                    <td>
                        <Link to={url_reverse("games:relationship", { game_id: el.target_id })}>
                            <Awesome type="list-ul" />
                        </Link>
                    </td>
                </>
            )}
        />
    );
}

const BundleItemList = ({ header, gameId }) => {
    const { t } = useTranslation();
    const { apiData, setApiData, setApiReget } = useAppApi(
        url_reverse("api:game:bundle_item:list", { game_id: gameId })
    );
    const { setDelData } = useAppContext();
    const [showModal, setShowModal] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData) return;

    const disableEdit =
        !commonInfo.account.is_super &&
        [GameVisibilityState.CATALOG, GameVisibilityState.PAGE].includes(gameCommonInfo.game?.visibility_state);

    const headerTools = (
        <Button size="sm" variant="dark" onClick={() => setShowModal(true)} disabled={disableEdit}>
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Button>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <InnerItemAddModal
                apiData={apiData}
                setApiData={setApiData}
                showModal={showModal}
                setShowModal={setShowModal}
            />
            <DndTable
                apiData={apiData}
                setApiData={setApiData}
                apiUrl={url_reverse("api:game:bundle_item:list", { game_id: gameId })}
                ths={
                    <>
                        <th>{t("Предмет")}</th>
                        <th />
                    </>
                }
                tds={(el) => (
                    <>
                        <td>
                            <Link
                                to={url_reverse("games:locale:edit", {
                                    game_id: el.target.id,
                                    locale: el.target.base_locale,
                                })}
                            >
                                {el.target.base_locale_object.name}
                            </Link>
                        </td>
                        <td className="dmg-table-row-tools">
                            {!disableEdit && (
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() =>
                                        setDelData({
                                            url: url_reverse(
                                                "api:game:bundle_item:list",
                                                {
                                                    game_id: gameId,
                                                },
                                                { ids: el.id }
                                            ),
                                            afterRespOK: () => {
                                                setApiReget(true);
                                            },
                                        })
                                    }
                                />
                            )}
                        </td>
                    </>
                )}
            />
        </ListingCard>
    );
};

// ---------- Demo ----------

function DemoBlock({ apiData }) {
    const { t } = useTranslation();
    const { setApiReget } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const gameId = gameCommonInfo.game.id;
    const { api_post } = useRequest();

    const addDemo = (ev) => {
        api_post(ev, url_reverse("api:game:demo:add", { game_id: gameId }), {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiReget(true);
                }
            },
        });
    };

    return (
        <Card className="mb-4">
            <Card.Header>
                {t("Демо")}
                {!apiData.demo && (
                    <div className="dmg-card-header-tools">
                        <DevForm.Button variant="dark" size="sm" name="add_demo" onClick={addDemo}>
                            <Awesome type="plus-circle" /> {t("Добавить")}
                        </DevForm.Button>
                    </div>
                )}
            </Card.Header>
            <Card.Body>
                {apiData.demo && (
                    <Table responsive className="dmg-table">
                        <thead>
                            <tr>
                                <th>{t("Название")}</th>
                                <th>{t("Публикация")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <Link
                                        to={url_reverse("games:locale:edit", {
                                            game_id: apiData.demo.target.id,
                                            locale: apiData.demo.target.base_locale,
                                        })}
                                    >
                                        {apiData.demo.target.base_locale_object.name}
                                    </Link>
                                </td>
                                <td>
                                    <YesNoColumn
                                        attr={apiData.demo.target.publication_state === GamePublicationState.DONE}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
}

// ---------- Dlc ----------

const DlcAdd = ({ gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:dlc:add", { game_id: gameId }));

    if (!apiData) return;

    return (
        <CommonCard header={t("Добавить DLC")}>
            <RenderForm backUrl={url_reverse("games:relationship", { game_id: gameId })} />
        </CommonCard>
    );
};

function DlcBlock({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const gameId = gameCommonInfo.game.id;
    const GameRelationshipKindTrans = useChoiceLabel("GameRelationshipKind");

    return (
        <Card className="mt-4">
            <Card.Header>
                DLC
                <div className="dmg-card-header-tools">
                    <Link
                        to={url_reverse("games:relationship:add", { game_id: gameId, rel_type: "dlc" })}
                        className="btn btn-sm btn-dark"
                    >
                        <Awesome type="plus-circle" /> {t("Добавить")}
                    </Link>
                </div>
            </Card.Header>
            <Card.Body>
                <DndTable
                    apiData={apiData}
                    setApiData={setApiData}
                    apiUrl={url_reverse("api:game:relationship", { game_id: gameId })}
                    listName="dlc_list"
                    postAction="rearrange_dlc"
                    ths={
                        <>
                            <th>{t("Название")}</th>
                            <th>{t("Тип")}</th>
                            <th>{t("На витрине")}</th>
                        </>
                    }
                    tds={(el) => (
                        <>
                            <td>
                                <Link
                                    to={url_reverse("games:locale:edit", {
                                        game_id: el.target.id,
                                        locale: el.target.base_locale,
                                    })}
                                >
                                    {el.target.base_locale_object.name}
                                </Link>
                            </td>
                            <td>{GameRelationshipKindTrans[el.kind]}</td>
                            <td>
                                <YesNoColumn attr={el.__is_active} />
                            </td>
                        </>
                    )}
                />
            </Card.Body>
        </Card>
    );
}

// ---------- Playtest ----------

const PlaytestAdd = ({ gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:dlc:add", { game_id: gameId }));

    if (!apiData) return;

    return (
        <CommonCard header={t("Добавить Playtest")}>
            <RenderForm backUrl={url_reverse("games:relationship", { game_id: gameId })} />
        </CommonCard>
    );
};

function PlaytestBlock({ apiData, setApiData }) {
    const { t } = useTranslation();
    const { setApiReget } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const gameId = gameCommonInfo.game.id;
    const { api_post } = useRequest();

    const addPlaytest = (ev) => {
        api_post(ev, url_reverse("api:game:playtest:add", { game_id: gameId }), {
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiReget(true);
                }
            },
        });
    };

    return (
        <Card className="mb-4">
            <Card.Header>
                Playtest
                <div className="dmg-card-header-tools">
                    <DevForm.Button variant="dark" size="sm" name="add_playtest" onClick={addPlaytest}>
                        <Awesome type="plus-circle" /> {t("Добавить")}
                    </DevForm.Button>
                </div>
            </Card.Header>
            <Card.Body>
                <DndTable
                    apiData={apiData}
                    setApiData={setApiData}
                    apiUrl={url_reverse("api:game:relationship", { game_id: gameId })}
                    listName="playtest_list"
                    postAction="rearrange_playtest"
                    ths={
                        <>
                            <th>{t("Название")}</th>
                            <th>{t("На витрине")}</th>
                        </>
                    }
                    tds={(el) => (
                        <>
                            <td>
                                <Link
                                    to={url_reverse("games:locale:edit", {
                                        game_id: el.target.id,
                                        locale: el.target.base_locale,
                                    })}
                                >
                                    {el.target.base_locale_object.name}
                                </Link>
                            </td>
                            <td>
                                <YesNoColumn attr={el.__is_active} />
                            </td>
                        </>
                    )}
                />
            </Card.Body>
        </Card>
    );
}

// ---------- Edition ----------

const EditionAdd = ({ gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:edition:add", { game_id: gameId }));

    if (!apiData) return;

    return (
        <CommonCard header={t("Добавить издание")}>
            <RenderForm backUrl={url_reverse("games:relationship", { game_id: gameId })} />
        </CommonCard>
    );
};

const EditionItemList = ({ header, gameId }) => {
    const { t } = useTranslation();
    const { apiData, setApiData, setApiReget } = useAppApi(
        url_reverse("api:game:edition_item:list", { game_id: gameId })
    );
    const { setDelData } = useAppContext();
    const [showModal, setShowModal] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData) return;

    const disableEdit = !commonInfo.account.is_super && gameCommonInfo.game?.date_published;

    const headerTools = (
        <Button size="sm" variant="dark" onClick={() => setShowModal(true)} disabled={disableEdit}>
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Button>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <InnerItemAddModal
                apiData={apiData}
                setApiData={setApiData}
                showModal={showModal}
                setShowModal={setShowModal}
            />
            <DndTable
                apiData={apiData}
                setApiData={setApiData}
                apiUrl={url_reverse("api:game:edition_item:list", { game_id: gameId })}
                lockedIds={apiData.object_list.filter((o) => o.__required_item).map((o) => o.id)}
                ths={
                    <>
                        <th>{t("Предмет")}</th>
                        <th />
                    </>
                }
                tds={(el) => (
                    <>
                        <td>
                            <Link
                                to={url_reverse("games:locale:edit", {
                                    game_id: el.target.id,
                                    locale: el.target.base_locale,
                                })}
                            >
                                {el.target.base_locale_object.name}
                            </Link>
                        </td>
                        <td className="dmg-table-row-tools">
                            {!disableEdit && !el.__required_item && (
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() =>
                                        setDelData({
                                            url: url_reverse(
                                                "api:game:edition_item:list",
                                                {
                                                    game_id: gameId,
                                                },
                                                { ids: el.id }
                                            ),
                                            afterRespOK: () => {
                                                setApiReget(true);
                                            },
                                        })
                                    }
                                />
                            )}
                        </td>
                    </>
                )}
            />
        </ListingCard>
    );
};

function EditionBlock({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const gameId = gameCommonInfo.game.id;

    return (
        <Card className="mb-4">
            <Card.Header>
                {t("Издания")}
                <div className="dmg-card-header-tools">
                    <Link
                        to={url_reverse("games:relationship:add", { game_id: gameId, rel_type: "edition" })}
                        className="btn btn-sm btn-dark"
                    >
                        <Awesome type="plus-circle" /> {t("Добавить")}
                    </Link>
                </div>
            </Card.Header>
            <Card.Body>
                <DndTable
                    apiData={apiData}
                    setApiData={setApiData}
                    apiUrl={url_reverse("api:game:relationship", { game_id: gameId })}
                    listName="edition_list"
                    postAction="rearrange_edition"
                    ths={
                        <>
                            <th>{t("Название")}</th>
                            <th>{t("На витрине")}</th>
                        </>
                    }
                    tds={(el) => (
                        <>
                            <td>
                                <Link
                                    to={url_reverse("games:locale:edit", {
                                        game_id: el.target.id,
                                        locale: el.target.base_locale,
                                    })}
                                >
                                    {el.target.base_locale_object.name}
                                </Link>
                            </td>
                            <td>
                                <YesNoColumn attr={el.__is_active} />
                            </td>
                        </>
                    )}
                />
            </Card.Body>
        </Card>
    );
}
